import React from "react";
import {
  IconAward,
  IconCancelTaxi,
  IconExchange,
  IconMoney,
  IconPoint,
  IconPriorityPath,
  IconTimeFill,
  IconTotalPoint,
  IconTotalPointCancel,
  IconWarning,
} from "@/components";
import { PointCategory, UnspecifiedSubCategory } from "@/features/common/PointHistory/type";
import { COLORS } from "@/utils/colors";
import { IconPointCarpool } from "@/components/IconPointCarpool";

/**
 * ポイント履歴のアイテムの設定情報
 */
export type PointItemConfig = {
  icon: React.ReactElement;
  iconColor: string;
  logColor: string;
};

/**
 * カテゴリごとのポイント履歴アイテム設定を取得する
 * アイコン、アイコンの色、ログ情報の色を含む
 *
 * @param category - ポイントカテゴリ
 * @param unspecifiedSubCategory - UNSPECIFIED専用のサブカテゴリ (categoryがUNSPECIFIEDの場合のみ使用)
 * @param isMinus - ポイントがマイナスかどうか
 * @returns ポイントアイテムの設定情報
 */
export const getPointItemConfig = (
  category: PointCategory,
  unspecifiedSubCategory: UnspecifiedSubCategory | null,
  isMinus: boolean
): PointItemConfig => {
  const logColor = isMinus ? COLORS.DEFICIT : COLORS.TEXT_WEAK;

  switch (category) {
    case "UNSPECIFIED":
      if (unspecifiedSubCategory === null) {
        throw new Error("UnspecifiedSubCategory is required when category is UNSPECIFIED");
      }

      switch (unspecifiedSubCategory) {
        case "Exchanged":
          return {
            icon: React.createElement(IconExchange),
            iconColor: COLORS.PRIMARY,
            logColor: COLORS.TEXT_WEAK,
          };

        case "Reverted":
          return {
            icon: React.createElement(IconExchange),
            iconColor: COLORS.NEGATIVE,
            logColor: COLORS.DEFICIT,
          };

        case "ErrorFee":
          return {
            icon: React.createElement(IconWarning),
            iconColor: COLORS.NEGATIVE,
            logColor: COLORS.DEFICIT,
          };

        case "Transfered":
          return {
            icon: React.createElement(IconTotalPoint),
            iconColor: isMinus ? COLORS.NEGATIVE : COLORS.SECONDARY,
            logColor: isMinus ? COLORS.DEFICIT : COLORS.TEXT_WEAK,
          };

        case "TransferReverted":
          return {
            icon: React.createElement(IconTotalPointCancel),
            iconColor: isMinus ? COLORS.NEGATIVE : COLORS.SECONDARY,
            logColor: isMinus ? COLORS.DEFICIT : COLORS.TEXT_WEAK,
          };

        default: {
          const _exhaustiveCheck: never = unspecifiedSubCategory;
          throw new Error(`Unknown UnspecifiedSubCategory: ${_exhaustiveCheck}`);
        }
      }

    case "USER_TIP":
      return {
        icon: React.createElement(IconMoney),
        iconColor: isMinus ? COLORS.NEGATIVE : COLORS.ACCENT,
        logColor,
      };

    case "USER_CANCEL_PAYMENT":
      return {
        icon: React.createElement(IconCancelTaxi),
        iconColor: isMinus ? COLORS.NEGATIVE : COLORS.SECONDARY,
        logColor,
      };

    case "PRIORITY_PASS":
      return {
        icon: React.createElement(IconPriorityPath),
        iconColor: isMinus ? COLORS.NEGATIVE : COLORS.SECONDARY,
        logColor,
      };

    case "PICK_UP":
      return {
        icon: React.createElement(IconPoint),
        iconColor: isMinus ? COLORS.NEGATIVE : COLORS.SECONDARY,
        logColor,
      };

    case "RESERVATION":
      return {
        icon: React.createElement(IconTimeFill),
        iconColor: isMinus ? COLORS.NEGATIVE : COLORS.SECONDARY,
        logColor,
      };

    case "CARPOOL_PICK_UP":
    case "CARPOOL_RESERVATION_COUNT":
    case "CARPOOL_LONG_PICKUP_DURATION":
      return {
        icon: React.createElement(IconPointCarpool),
        iconColor: isMinus ? COLORS.NEGATIVE : COLORS.SECONDARY,
        logColor,
      };

    case "SALES_DISTRIBUTION":
      return {
        icon: React.createElement(IconPoint),
        iconColor: isMinus ? COLORS.NEGATIVE : COLORS.SECONDARY,
        logColor,
      };

    case "AWARD":
      return {
        icon: React.createElement(IconAward),
        iconColor: isMinus ? COLORS.NEGATIVE : COLORS.SECONDARY,
        logColor,
      };

    default: {
      const _exhaustiveCheck: never = category;
      throw new Error(`Unknown PointCategory: ${_exhaustiveCheck}`);
    }
  }
};
